import { Fragment, useCallback, useId, useState } from 'react'
import type { CopyKey } from 'locales/copy-keys.gen'
import { ArrowSimple, Copy, t } from '@stokr/components-library'
import CrossIcon from '@stokr/components-library/dist/images/cross-icon.svg?react'
import DocIcon from 'images/docs-icon.svg?react'
import DownloadIcon from '@stokr/components-library/dist/images/download_icon.svg?react'
import { Section, SectionSkeleton } from 'components/sto/Section'
import { CardTabs, TabCard, TabCardBody, TabPanel } from 'components/sto/Tabs'
import { SectionHeading } from 'components/sto/Section/Section.styles'
import { LockIcon } from 'components/ui/icons'
import { useStoPageActions } from 'context/StoPageActionsContext'
import { useDocuments, type DocumentGroupId, type DocumentsView, type StoDocument } from 'hooks/sto/useDocuments'
import { useDocumentDownload } from 'hooks/sto/useDocumentDownload'
import { useStoTrack } from 'hooks/sto/useStoTracking'
import {
  AccessBadge,
  Content,
  Divider,
  DocumentsHeading,
  DocumentsIntro,
  DrawerBody,
  DrawerHeader,
  FileTile,
  ListCard,
  LoginLink,
  PromptText,
  RoundButton,
  DownloadButton,
  DownloadText,
  Row,
  RowError,
  RowMeta,
  RowText,
  RowTitle,
  TermsText,
  StoSideModal,
  ViewAllButton,
} from './Documents.styles'

/** Rows shown in a tab (Figma 3711:10472); the rest open in the drawer, that tab's only. */
const PREVIEW_COUNT = 5

const GROUP_LABEL: Record<DocumentGroupId, CopyKey> = {
  offering: 'stoPage.documents.tabs.offering',
  other: 'stoPage.documents.tabs.other',
}

/** `PDF - 10 Sep 2026`. File size is intentionally absent — no such field exists. */
const metaLine = (document: StoDocument): string => [document.format, document.date].filter(Boolean).join(' - ')

interface DocumentRowsProps {
  documents: StoDocument[]
  truncate?: boolean
  group: string
  download: ReturnType<typeof useDocumentDownload>
}

/** The rows, in the tab and in the drawer alike. */
const DocumentRows = ({ documents, group, truncate = false, download }: DocumentRowsProps) => (
  <>
    {documents.map((document, index) => (
      <Fragment key={document.id}>
        {index > 0 && <Divider />}
        <Row>
          <FileTile aria-hidden="true">
            <DocIcon width={24} height={24} />
          </FileTile>

          <RowText>
            <RowTitle $truncate={truncate} title={truncate ? document.title : undefined}>
              {document.title}
            </RowTitle>
            <RowMeta>
              {download.failedId === document.id ? (
                <RowError role="alert">
                  <Copy id="stoPage.documents.downloadFailed" />
                </RowError>
              ) : (
                metaLine(document)
              )}
            </RowMeta>
          </RowText>

          {document.isRestricted && (
            <AccessBadge>
              <LockIcon width={16} height={16} aria-hidden="true" />
              <Copy id="stoPage.documents.badgeRestricted" />
            </AccessBadge>
          )}

          <DownloadButton
            $size={36}
            type="button"
            disabled={!document.canDownload || download.downloadingId === document.id}
            aria-label={t('stoPage.documents.downloadLabel', { name: document.title })}
            data-cy={`sto-document-download-${document.id}`}
            onClick={() =>
              download.download({
                id: document.id,
                fileName: document.fileName,
                isPublic: document.isPublic,
                group,
                location: truncate ? 'drawer' : 'preview',
              })
            }>
            <DownloadText aria-hidden="true">
              <Copy id="stoPage.documents.download" />
            </DownloadText>
            <DownloadIcon width={16} height={16} aria-hidden="true" />
          </DownloadButton>
        </Row>
      </Fragment>
    ))}
  </>
)

const LoginPrompt = ({ onLogin, location }: { onLogin: () => void; location: 'documents' | 'documents_drawer' }) => {
  const trackSto = useStoTrack()
  return (
    <PromptText>
      <Copy id="stoPage.documents.loginPromptBefore" />
      <LoginLink
        type="button"
        data-cy={`sto-${location.replace('_', '-')}-login`}
        onClick={() => {
          trackSto('sto_login_clicked', { location })
          onLogin()
        }}>
        <Copy id="stoPage.documents.loginPromptLink" />
      </LoginLink>
      <Copy id="stoPage.documents.loginPromptAfter" />
    </PromptText>
  )
}

/** "Documents", the login prompt, and the documents in tabs by type; a tab's full list in a drawer. */
const DocumentTabs = ({ view }: { view: DocumentsView }) => {
  const { openLoginModal } = useStoPageActions()
  const trackSto = useStoTrack()
  const download = useDocumentDownload()
  const panelId = useId()
  const drawerTitleId = useId()
  const [picked, setPicked] = useState<DocumentGroupId>()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), [])
  // The login modal must not open underneath the drawer.
  const loginFromDrawer = useCallback(() => {
    setIsDrawerOpen(false)
    openLoginModal()
  }, [openLoginModal])

  const active = view.groups.find((group) => group.id === picked) ?? view.groups[0]

  return (
    <Content>
      <DocumentsIntro>
        <DocumentsHeading>
          <Copy id="stoPage.documents.title" />
        </DocumentsHeading>
        {view.showLoginPrompt && <LoginPrompt onLogin={openLoginModal} location="documents" />}
      </DocumentsIntro>

      <TabCard>
        <CardTabs
          tabs={view.groups.map((group) => ({
            id: group.id,
            label: t(GROUP_LABEL[group.id]),
            domId: `documents-${group.id}`,
          }))}
          activeId={active.id}
          onSelect={(id) => {
            if (id !== active.id) trackSto('sto_documents_tab_selected', { tab: id })
            setPicked(id)
          }}
          panelId={panelId}
          label={t('stoPage.documents.title')}
          fit
          dataCy="sto-documents-tab"
        />
        <TabPanel activeKey={active.id} id={panelId} aria-labelledby={`documents-${active.id}`}>
          <TabCardBody>
            <DocumentRows documents={active.documents.slice(0, PREVIEW_COUNT)} group={active.id} download={download} />
            {active.documents.length > PREVIEW_COUNT && (
              <ViewAllButton
                type="button"
                black
                data-cy="sto-documents-view-all"
                onClick={() => {
                  trackSto('sto_documents_view_all_clicked', { tab: active.id, count: active.documents.length })
                  setIsDrawerOpen(true)
                }}
                aria-haspopup="dialog">
                <Copy id="stoPage.documents.viewFullList" />
                <ArrowSimple direction="right" width={10} height={6} aria-hidden="true" />
              </ViewAllButton>
            )}
          </TabCardBody>
        </TabPanel>
      </TabCard>

      {/* Library SideModal sizes in % with a px minimum: 0% lets minWidth give Figma's 704px.
          Its own close button is replaced by the Figma header. */}
      <StoSideModal
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        showCloseButton={false}
        width={0}
        minWidth={704}
        role="dialog"
        aria-modal="true"
        aria-labelledby={drawerTitleId}>
        <DrawerBody>
          <DrawerHeader>
            <SectionHeading id={drawerTitleId}>
              <Copy id="stoPage.documents.drawerTitle" values={{ group: t(GROUP_LABEL[active.id]) }} />
            </SectionHeading>
            <RoundButton
              $size={40}
              type="button"
              aria-label={t('stoPage.documents.closeList')}
              data-cy="sto-documents-drawer-close"
              onClick={closeDrawer}>
              <CrossIcon width={12} height={12} aria-hidden="true" />
            </RoundButton>
          </DrawerHeader>
          <Content>
            {view.showLoginPrompt && <LoginPrompt onLogin={loginFromDrawer} location="documents_drawer" />}
            <ListCard>
              <DocumentRows documents={active.documents} group={active.id} truncate download={download} />
            </ListCard>
          </Content>
        </DrawerBody>
      </StoSideModal>
    </Content>
  )
}

/**
 * Terms & Conditions with the documents under it (Figma 3711:10472), one card. The terms are
 * static copy, always shown (keeping the `#terms` anchor); without documents only they show.
 */
export const Documents = () => {
  const state = useDocuments()

  return (
    <Section
      anchor="documents"
      title={<Copy id="stoPage.terms.title" />}
      state={{ status: 'ready', data: null }}
      collapsible="always">
      {() => (
        <Content $gap={8}>
          <TermsText id="terms">
            <Copy id="stoPage.terms.text" />
          </TermsText>
          {state.status === 'loading' && <SectionSkeleton rows={3} />}
          {state.status === 'ready' && <DocumentTabs view={state.data} />}
        </Content>
      )}
    </Section>
  )
}
